import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../utils/pageSeo';
import databridgeSvgDark from '../assets/databridge-diagram.svg';
import databridgeSvgLight from '../assets/databridge-diagram-light.svg';
import '../styles/productPage.css';

const TABS = ['overview', 'connectors', 'architecture', 'gettingStarted'];

const HIGHLIGHT_ICONS = {
  governed: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  aiready: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  connectors: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/>
      <rect x="2" y="14" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/>
      <line x1="10" y1="6" x2="14" y2="6"/><line x1="10" y1="18" x2="14" y2="18"/>
      <line x1="6" y1="10" x2="6" y2="14"/><line x1="18" y1="10" x2="18" y2="14"/>
    </svg>
  ),
  security: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
};

const AIDataBridge = () => {
  const { t } = useTranslation('aiDataBridge');
  const { language } = useLanguage();
  const seo = PAGE_SEO.e1DataBridge;
  const { theme } = useTheme();
  const databridgeSvg = theme === 'light' ? databridgeSvgLight : databridgeSvgDark;
  const [activeTab, setActiveTab] = useState(0);
  const tabsSectionRef = useRef(null);

  const goToTab = (index) => {
    setActiveTab(index);
    requestAnimationFrame(() => {
      if (tabsSectionRef.current) {
        const top = tabsSectionRef.current.offsetTop - 105;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  };

  return (
    <div className="product-page">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={`/${language}/e1-databridge`}
      />

      {/* Hero */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section" ref={tabsSectionRef}>
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => goToTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>

                <div className="pp-overview-highlights">
                  {[
                    { k: 'h1', icon: HIGHLIGHT_ICONS.governed },
                    { k: 'h2', icon: HIGHLIGHT_ICONS.aiready },
                    { k: 'h3', icon: HIGHLIGHT_ICONS.connectors },
                    { k: 'h4', icon: HIGHLIGHT_ICONS.security },
                  ].map(({ k, icon }) => (
                    <div className="pp-overview-card" key={k}>
                      <div className="pp-overview-card-icon">{icon}</div>
                      <div className="pp-overview-card-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-overview-card-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Connectors ─────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('connectors.tag')}</span>
                <h2 className="pp-section-heading">{t('connectors.heading')}</h2>
                <p className="pp-section-desc">{t('connectors.description')}</p>

                <div className="pp-connector-grid">
                  {['crm','erp','hrms','internalApis','eventStreams','documentSources'].map(key => (
                    <div className="pp-connector-card" key={key}>
                      <span className="pp-feature-tag">{t(`connectors.items.${key}.tag`)}</span>
                      <div className="pp-connector-title">{t(`connectors.items.${key}.title`)}</div>
                      <div className="pp-connector-desc">{t(`connectors.items.${key}.description`)}</div>
                      {['crm','erp','hrms','eventStreams','documentSources'].includes(key) && (
                        <div className="pp-connector-examples">{t(`connectors.items.${key}.examples`)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Architecture ───────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('architecture.tag')}</span>
                <h2 className="pp-section-heading">{t('architecture.heading')}</h2>
                <p className="pp-section-desc">{t('architecture.description')}</p>

                <div className="pp-arch-stages">
                  {['extract','validate','transform','load'].map(key => (
                    <div className="pp-arch-stage" key={key}>
                      <span className="pp-arch-stage-step">{t(`architecture.stages.${key}.step`)}</span>
                      <span className="pp-arch-stage-tag">{t(`architecture.stages.${key}.tag`)}</span>
                      <div className="pp-arch-stage-title">{t(`architecture.stages.${key}.title`)}</div>
                      <div className="pp-arch-stage-desc">{t(`architecture.stages.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <img
                  src={databridgeSvg}
                  alt="E1 DataBridge Architecture Diagram"
                  style={{ width: '100%', borderRadius: '12px', marginTop: '2rem' }}
                />
              </div>
            )}

            {/* ── Getting Started ────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('gettingStarted.tag')}</span>
                <h2 className="pp-section-heading">{t('gettingStarted.heading')}</h2>

                <div className="pp-onboarding-steps">
                  {['step1','step2','step3'].map(key => (
                    <div className="pp-onboarding-step" key={key}>
                      <span className="pp-onboarding-num">{t(`gettingStarted.steps.${key}.number`)}</span>
                      <div className="pp-onboarding-title">{t(`gettingStarted.steps.${key}.title`)}</div>
                      <div className="pp-onboarding-desc">{t(`gettingStarted.steps.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <div className="pp-cta-block">
                  <div className="pp-cta-heading">{t('gettingStarted.cta.heading')}</div>
                  <p className="pp-cta-desc">{t('gettingStarted.cta.description')}</p>
                  <Link to={`/${language}/contact`} className="pp-cta-btn">
                    {t('gettingStarted.cta.button')}
                  </Link>
                </div>
              </div>
            )}

            {/* ── Tab Navigation ─────────────────────────── */}
            <div className="pp-tab-nav">
              {activeTab > 0 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-prev" onClick={() => goToTab(activeTab - 1)}>
                  <span className="pp-tab-nav-dir">← Prev</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab - 1]}`)}</span>
                </button>
              ) : <span />}
              {activeTab < TABS.length - 1 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-next" onClick={() => goToTab(activeTab + 1)}>
                  <span className="pp-tab-nav-dir">Next →</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab + 1]}`)}</span>
                </button>
              ) : <span />}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AIDataBridge;
